import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { RequestListItemDto, RequestStatus } from "@gw/shared-types";
import { InfoTooltip } from "@/components/InfoTooltip";
import { BrandedLoader } from "@/components/BrandedLoader";
import { fmtETDate } from "@/lib/format";
import { useDelayedFlag } from "@/lib/useDelayedFlag";
import { FilterRail } from "@/features/requestList/components/FilterRail";
import { FilterGroup } from "@/features/requestList/components/FilterGroup";
import { SortHead, type SortState } from "@/features/requestList/components/SortHead";
import { StatusPill } from "@/features/requestList/components/StatusPill";
import { useThroughputUrlState } from "./useThroughputUrlState";
import { useThroughputQuery, useThroughputInstancesQuery } from "./useThroughputQuery";
import { ThroughputChart, ThroughputLegend, shortMonth, type ChartMode } from "./components/ThroughputChart";
import type { ThroughputBucketDto, ThroughputGroupRowDto } from "@/api/endpoints";
import { AssistantPanel } from "@/features/assistant/AssistantPanel";

const WORKERS_TIP =
  "A worker can have multiple work orders (timesheet-approval cycles). Counted once per unique email; " +
  "period totals also de-duplicate across months.";
const COMPLETED_TIP =
  "Bucketed by the date the work order was approved — stable month to month, unlike start-date reports.";
const RATIO_TIP =
  "Completed-in-period over started-in-period. Different cohorts, so it can exceed 100% when backlog clears.";
const BACKLOG_TIP =
  "Requests created on or before the end of the window that were not yet approved by then.";

const BREAKDOWNS: { key: "" | "client" | "lob" | "engagementType" | "assignedTo"; label: string }[] = [
  { key: "", label: "Overall" },
  { key: "client", label: "Client" },
  { key: "lob", label: "LOB" },
  { key: "engagementType", label: "Engagement" },
  { key: "assignedTo", label: "Assigned to" },
];
const breakdownLabel = (key: string) => BREAKDOWNS.find((b) => b.key === key)?.label ?? "Group";

const MONTH_PRESETS = [3, 6];

// --- period helpers (month strings "YYYY-MM") ---
function pad(n: number): string {
  return n < 10 ? "0" + n : String(n);
}
function lastOfMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return `${month}-${pad(new Date(y ?? 2000, m ?? 1, 0).getDate())}`;
}
function resolveWindow(months: number, from?: string, to?: string) {
  const now = new Date();
  const curMonth = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const fromMonth = from || (() => {
    const s = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
    return `${s.getFullYear()}-${pad(s.getMonth() + 1)}`;
  })();
  const toMonth = to || curMonth;
  return {
    fromMonth,
    toMonth,
    apiFrom: `${fromMonth}-01`,
    apiTo: lastOfMonth(toMonth),
    label: `${shortMonth(fromMonth)} – ${shortMonth(toMonth)}`,
  };
}

interface Row {
  key: string;
  label: string;
  started: number;
  workersStarted: number;
  completed: number;
  workersCompleted: number;
  openAtEnd: number;
  ratio: number | null;
}

const ratioOf = (started: number, completed: number) => (started ? completed / started : null);
const fmtRatio = (r: number | null) => (r != null ? Math.round(r * 100) + "%" : "—");
const monthToRow = (m: ThroughputBucketDto): Row => ({
  key: m.period, label: shortMonth(m.period),
  started: m.startedCount, workersStarted: m.startedWorkers,
  completed: m.completedCount, workersCompleted: m.completedWorkers,
  openAtEnd: m.openAtEnd, ratio: ratioOf(m.startedCount, m.completedCount),
});
const groupToRow = (g: ThroughputGroupRowDto): Row => ({
  key: g.groupValue || "—", label: g.groupValue || "—",
  started: g.startedCount, workersStarted: g.startedWorkers,
  completed: g.completedCount, workersCompleted: g.completedWorkers,
  openAtEnd: g.openAtEnd, ratio: ratioOf(g.startedCount, g.completedCount),
});

// Human noun for the active Request-type selection, used across the screen's labels.
function typeNoun(onboardingsOnly: boolean, requestType: string[]): string {
  const parts: string[] = [];
  if (onboardingsOnly) parts.push("Onboardings");
  if (requestType.some((t) => t.toLowerCase().includes("extension"))) parts.push("Extensions");
  return parts.length ? parts.join(" and ") : "Requests";
}

export function ThroughputScreen() {
  const navigate = useNavigate();
  const [state, setState] = useThroughputUrlState();
  const noun = typeNoun(state.onboardingsOnly, state.requestType);
  const win = useMemo(
    () => resolveWindow(state.months, state.from, state.to),
    [state.months, state.from, state.to],
  );

  const filters = {
    from: win.apiFrom,
    to: win.apiTo,
    onboardingsOnly: state.onboardingsOnly,
    redeployment: state.redeployment,
    requestType: state.requestType,
    client: state.client,
    lob: state.lob,
    eng: state.eng,
    assigned: state.assigned,
    country: state.country,
  };

  const query = useThroughputQuery({ ...filters, groupBy: state.breakdown || undefined });
  const data = query.data;

  // Rail filter options come bundled in the throughput response — one fetch drives the whole screen.
  const opts = data?.filterOptions;

  const instancesEnabled = state.tab === "requests";
  const instancesQuery = useThroughputInstancesQuery(
    {
      ...filters,
      basis: state.basis,
      month: state.drillMonth,
      groupBy: state.drillSegKey || undefined,
      groupValue: state.drillSegVal,
    },
    instancesEnabled,
  );

  // Branded loader over the content while the active tab's data is in flight (same as other screens).
  const fetching = state.tab === "requests" ? instancesQuery.isFetching : query.isFetching;
  const showLoader = useDelayedFlag(fetching);

  const sort: SortState = { key: state.bdSortKey, dir: state.bdSortDir };
  const setSort = (s: SortState) => setState({ bdSortKey: s.key, bdSortDir: s.dir });

  const rows: Row[] = useMemo(() => {
    if (!data) return [];
    const base = state.breakdown ? (data.byGroup ?? []).map(groupToRow) : data.months.map(monthToRow);
    const mult = state.bdSortDir === "asc" ? 1 : -1;
    const key = state.bdSortKey as keyof Row;
    return [...base].sort((a, b) => {
      const av = (a[key] ?? -1) as number;
      const bv = (b[key] ?? -1) as number;
      return av < bv ? -1 * mult : av > bv ? 1 * mult : 0;
    });
  }, [data, state.breakdown, state.bdSortKey, state.bdSortDir]);

  const drillToMonth = (period: string) =>
    setState({ tab: "requests", basis: "started", drillMonth: period, drillSegKey: "", drillSegVal: undefined });
  const drillToSegment = (value: string) =>
    setState({ tab: "requests", basis: "started", drillSegKey: state.breakdown, drillSegVal: value, drillMonth: undefined });

  const clearAll = () =>
    setState({
      onboardingsOnly: true, redeployment: false,
      requestType: [], client: [], lob: [], eng: [], assigned: [], country: [],
      months: 3, from: undefined, to: undefined,
    });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Throughput</h1>
          <div className="subtitle">
            {noun} <strong>started</strong> (by created date) vs <strong>completed</strong> (by Work
            Order approved date), with unique workers — stable month-over-month reporting.{" "}
            <span style={{ color: "var(--fg-3)" }}>{win.label}</span>
          </div>
        </div>
      </div>

      <div className="screen-body">
        {showLoader && <BrandedLoader />}

        <div className="list-layout">
        <div className="rail-dock">
        <FilterRail onClear={clearAll} storageKey="throughput">
          <div className="filter-group">
            <div className="label">Period</div>
            <div className="date-presets">
              {MONTH_PRESETS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={!state.from && !state.to && state.months === n ? "active" : ""}
                  onClick={() => setState({ months: n, from: undefined, to: undefined })}
                >
                  {n}m
                </button>
              ))}
            </div>
            <div className="date-custom" style={{ marginTop: 8 }}>
              <label>
                From{" "}
                <input
                  type="month"
                  value={state.from ?? win.fromMonth}
                  onChange={(e) => setState({ from: e.target.value || undefined, to: state.to ?? win.toMonth })}
                />
              </label>
              <label>
                To{" "}
                <input
                  type="month"
                  value={state.to ?? win.toMonth}
                  onChange={(e) => setState({ from: state.from ?? win.fromMonth, to: e.target.value || undefined })}
                />
              </label>
              {(state.from || state.to) && (
                <button className="date-custom-clear" onClick={() => setState({ from: undefined, to: undefined })}>
                  Reset
                </button>
              )}
            </div>
          </div>

          {opts && (
            <>
              <FilterGroup
                label="Request type"
                // Only the Onboarding (≥7) bucket and Extension Request are meaningful here.
                options={opts.requestTypes.filter((o) => o.value.toLowerCase().includes("extension"))}
                selected={state.requestType}
                onChange={(arr) => setState({ requestType: arr })}
                formatValue={(v) => (v.toLowerCase().includes("extension") ? "Extension" : v)}
                extraOptions={[
                  { label: "Onboarding", checked: state.onboardingsOnly, onToggle: () => setState({ onboardingsOnly: !state.onboardingsOnly }) },
                ]}
              />
              <FilterGroup label="Client" options={opts.clients} selected={state.client} onChange={(arr) => setState({ client: arr })} searchable />
              <FilterGroup label="Line of business" options={opts.linesOfBusiness} selected={state.lob} onChange={(arr) => setState({ lob: arr })} />
              <FilterGroup label="Engagement type" options={opts.engagementTypes} selected={state.eng} onChange={(arr) => setState({ eng: arr })} />
              <FilterGroup label="Assigned to" options={opts.assignedUsers} selected={state.assigned} onChange={(arr) => setState({ assigned: arr })} searchable />
            </>
          )}
        </FilterRail>
        <AssistantPanel screen="throughput" activeFilters={state} />
        </div>

        <div className="card">
          <div className="tabs">
            <div className={"tab " + (state.tab === "overview" ? "active" : "")} onClick={() => setState({ tab: "overview" })}>
              Overview
            </div>
            <div className={"tab " + (state.tab === "requests" ? "active" : "")} onClick={() => setState({ tab: "requests" })}>
              Requests{state.drillMonth || state.drillSegVal ? " · filtered" : ""}
            </div>
          </div>

          {state.tab === "overview" && data && (
            <div className="tp-body">
              <div className="tp-kpis cards">
                <Kpi label={`${noun} started`} value={data.totals.startedCount} sub="by created date" />
                <Kpi label="Unique workers started" value={data.totals.startedWorkers} sub="deduped on email" info={WORKERS_TIP} />
                <Kpi label={`${noun} completed`} value={data.totals.completedCount} sub="by WO approved date" accent="var(--gw-success-600)" info={COMPLETED_TIP} />
                <Kpi label="Unique workers completed" value={data.totals.completedWorkers} sub="deduped on email" accent="var(--gw-success-600)" />
                <Kpi label="Completion ratio" value={fmtRatio(ratioOf(data.totals.startedCount, data.totals.completedCount))} sub="completed ÷ started, same period" info={RATIO_TIP} />
                <Kpi label="Open at period end" value={data.totals.openAtEnd} sub="backlog carried forward" accent="var(--gw-warning-700)" info={BACKLOG_TIP} />
              </div>

              <div className="tp-chart-head">
                <h3 className="tp-h3">By month</h3>
                <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                  <ThroughputLegend showBacklog={state.showBacklog} />
                  <div className="unit-toggle">
                    {(["grouped", "overlay", "line"] as ChartMode[]).map((m) => (
                      <button key={m} className={state.chartMode === m ? "on" : ""} onClick={() => setState({ chartMode: m })}>
                        {m.charAt(0).toUpperCase() + m.slice(1)}
                      </button>
                    ))}
                  </div>
                  <label style={{ fontSize: 12, color: "var(--fg-2)", display: "inline-flex", gap: 4, alignItems: "center" }}>
                    <input type="checkbox" checked={state.showBacklog} onChange={() => setState({ showBacklog: !state.showBacklog })} />
                    Backlog
                  </label>
                </div>
              </div>

              <ThroughputChart
                months={data.months}
                mode={state.chartMode}
                showBacklog={state.showBacklog}
                onPick={drillToMonth}
                activePeriod={state.drillMonth}
              />

              <div className="tp-bd-head">
                <span className="lbl">Break down by</span>
                <div className="tp-bd-chips">
                  {BREAKDOWNS.map((b) => (
                    <span
                      key={b.key || "overall"}
                      className={"groupby-chip " + (state.breakdown === b.key ? "active" : "")}
                      onClick={() => setState({ breakdown: b.key })}
                    >
                      {b.label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="tbl-wrap">
                <table className="tbl tp-tbl">
                  <thead>
                    <tr>
                      <th>{state.breakdown ? breakdownLabel(state.breakdown) : "Month"}</th>
                      <SortHead label="Started" sortKey="started" sort={sort} setSort={setSort} numeric />
                      <SortHead label="Workers" sortKey="workersStarted" sort={sort} setSort={setSort} numeric />
                      <SortHead label="Completed" sortKey="completed" sort={sort} setSort={setSort} numeric />
                      <SortHead label="Workers" sortKey="workersCompleted" sort={sort} setSort={setSort} numeric />
                      <SortHead label="Ratio" sortKey="ratio" sort={sort} setSort={setSort} numeric />
                      <SortHead label="Open (end)" sortKey="openAtEnd" sort={sort} setSort={setSort} numeric />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr
                        key={r.key}
                        onClick={() => (state.breakdown ? drillToSegment(r.key) : drillToMonth(r.key))}
                      >
                        <td><span className="cell-primary">{r.label}</span></td>
                        <td className="numeric">{r.started}</td>
                        <td className="numeric"><span className="cell-secondary">{r.workersStarted}</span></td>
                        <td className="numeric" style={{ color: "var(--gw-success-700)" }}>{r.completed}</td>
                        <td className="numeric"><span className="cell-secondary">{r.workersCompleted}</span></td>
                        <td className="numeric"><span className="cell-secondary">{fmtRatio(r.ratio)}</span></td>
                        <td className="numeric" style={{ color: r.openAtEnd ? "var(--gw-warning-700)" : "inherit" }}>{r.openAtEnd}</td>
                      </tr>
                    ))}
                    {!state.breakdown && (
                      <tr className="tp-total">
                        <td><strong>Total</strong></td>
                        <td className="numeric"><strong>{data.totals.startedCount}</strong></td>
                        <td className="numeric"><strong>{data.totals.startedWorkers}</strong></td>
                        <td className="numeric"><strong style={{ color: "var(--gw-success-700)" }}>{data.totals.completedCount}</strong></td>
                        <td className="numeric"><strong>{data.totals.completedWorkers}</strong></td>
                        <td className="numeric"><strong>{fmtRatio(ratioOf(data.totals.startedCount, data.totals.completedCount))}</strong></td>
                        <td className="numeric"><strong style={{ color: data.totals.openAtEnd ? "var(--gw-warning-700)" : "inherit" }}>{data.totals.openAtEnd}</strong></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="pagination">
                <div>{win.label} · {data.totals.startedCount.toLocaleString()} started · {data.totals.completedCount.toLocaleString()} completed</div>
                <div style={{ fontSize: 11, color: "var(--fg-3)" }}>Click a {state.breakdown ? "row" : "month"} to see the underlying requests</div>
              </div>
            </div>
          )}

          {state.tab === "requests" && (
            <RequestsTab
              noun={noun}
              basis={state.basis}
              setBasis={(b) => setState({ basis: b })}
              drillMonth={state.drillMonth}
              drillSegKey={state.drillSegKey}
              drillSegVal={state.drillSegVal}
              clearDrill={() => setState({ drillMonth: undefined, drillSegKey: "", drillSegVal: undefined })}
              periodLabel={win.label}
              items={instancesQuery.data?.items ?? []}
              totalCount={instancesQuery.data?.totalCount ?? 0}
              loading={instancesQuery.isPending}
              onRow={(id) => navigate(`/requests/${id}`)}
            />
          )}
        </div>
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, sub, accent, info }: { label: string; value: number | string; sub?: string; accent?: string; info?: string }) {
  return (
    <div className="tile">
      <div className="tile-label">
        {label}
        {info && <InfoTooltip inline text={info} />}
      </div>
      <div className="tile-value" style={accent ? { color: accent } : undefined}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {sub && <div style={{ fontSize: 12, color: "var(--fg-2)" }}>{sub}</div>}
    </div>
  );
}

const basesFor = (noun: string): { key: "started" | "completed" | "open"; label: string; tip: string }[] => [
  { key: "started", label: "Started", tip: `${noun} whose created date falls in the period — what began in this window.` },
  { key: "completed", label: "Completed", tip: `${noun} whose Work Order approved date falls in the period — what actually finished, regardless of when they started.` },
  { key: "open", label: "Open at end", tip: "Created on or before the end of the period but not yet approved by then — the backlog still in flight." },
];

function RequestsTab(props: {
  noun: string;
  basis: "started" | "completed" | "open";
  setBasis: (b: "started" | "completed" | "open") => void;
  drillMonth?: string;
  drillSegKey: string;
  drillSegVal?: string;
  clearDrill: () => void;
  periodLabel: string;
  items: RequestListItemDto[];
  totalCount: number;
  loading: boolean;
  onRow: (id: string) => void;
}) {
  const { basis, items, totalCount, noun } = props;
  const hasDrill = !!(props.drillMonth || props.drillSegVal);
  return (
    <>
      <div className="tp-req-toolbar">
        <span className="tp-basis-lbl">Show {noun.toLowerCase()}</span>
        <div className="unit-toggle">
          {basesFor(noun).map((b) => (
            <button key={b.key} className={basis === b.key ? "on" : ""} title={b.tip} onClick={() => props.setBasis(b.key)}>
              {b.label}
            </button>
          ))}
        </div>
        {hasDrill && (
          <div className="chip-row" style={{ marginLeft: 4 }}>
            {props.drillMonth && <span className="chip">Month: {shortMonth(props.drillMonth)}</span>}
            {props.drillSegVal && <span className="chip">{breakdownLabel(props.drillSegKey)}: {props.drillSegVal}</span>}
            <span className="chip clear-all" onClick={props.clearDrill}>Clear</span>
          </div>
        )}
      </div>
      <div className="tbl-wrap" style={{ maxHeight: "calc(100vh - 360px)" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Worker</th><th>Client</th><th>LOB</th><th>Eng.</th><th>Assigned to</th>
              <th>Type</th><th>Created</th><th>Approved</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={9}><div className="empty"><h4>{props.loading ? "Loading…" : "No requests"}</h4><p>{props.loading ? "" : "Nothing matches this selection."}</p></div></td></tr>
            )}
            {items.map((r) => (
              <tr key={r.id} onClick={() => props.onRow(r.id)}>
                <td>
                  <div className="cell-primary">{r.workerName}</div>
                  <div className="cell-secondary">{r.workOrderNumber}</div>
                </td>
                <td>{r.client}</td>
                <td><span className="cell-secondary">{r.lineOfBusiness}</span></td>
                <td><span className="cell-secondary">{r.engagementType}</span></td>
                <td><span className="cell-secondary">{r.assignedToUserName}</span></td>
                <td><span className="cell-secondary">{r.requestType}</span></td>
                <td><span className="cell-secondary">{fmtETDate(new Date(r.requestCreatedDateTime))}</span></td>
                <td>{r.workOrderApproveDatetime
                  ? <span className="cell-secondary">{fmtETDate(new Date(r.workOrderApproveDatetime))}</span>
                  : <span className="dur-running">In progress</span>}</td>
                <td><StatusPill status={(r.calculatedStatus ?? r.status) as RequestStatus} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <div>Showing {Math.min(250, items.length)} of {totalCount.toLocaleString()} requests · {basis} in {props.periodLabel}</div>
        <div style={{ fontSize: 11, color: "var(--fg-3)" }}>Click a row to open the request</div>
      </div>
    </>
  );
}
